"use client";

import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { Badge } from "../badge";
import { cx } from "../fortschritt/breite";
import { Hinweissatz } from "../formular/rueckmeldung";
import { Knopf } from "../knopf";
import { Zustandspille } from "../zustandspille";
import {
  alleAufklappen,
  dropZone,
  istNachfahre,
  sichtbareZeilen,
  tasteZuAktion,
  tasteZuVerschiebung,
  type BaumKnoten,
  type BaumOrdnen,
  type BaumVerschiebung,
  type DropZone,
  type SichtbareZeile,
} from "./baum-logik";

/**
 * Baum-Tabelle (M-003, Design-Guide `tabellen.md`, Archetyp B, Variante A = Eigenbau, entschieden 06.10.2026):
 * `<table role="treegrid">`, Zeilen mit `aria-level`, Aufklapp-Knopf (nicht die Zeile) mit `aria-expanded`,
 * Einrückung mit Linie über drei Ebenen (ab Ebene 5 flach, die Zweitzeile trägt den Pfad),
 * Pille „n Kinder“, „Alle aufklappen/zuklappen“ im Kopf, Tastatur nach WAI-ARIA treegrid (auf/ab, Home/End,
 * rechts öffnet bzw. springt ins erste Kind, links schließt bzw. springt zum Elternteil),
 * Nachladen der Kinder beim ersten Aufklappen (`onNachladen`) mit Lade- und Fehlerzeile,
 * optional Haupt-Link je Zeile (genau ein Anker, erweiterte Klickfläche) und Aktionsmenü.
 * Unter 768 px erscheinen die sichtbaren Zeilen als Karten (T12).
 * Der Baustein lädt nichts selbst: ohne `onNachladen` müssen alle Kinder in `kinder` stehen (Fassung 1, HRWorks-Test).
 */
const RAHMEN =
  "border border-table-line rounded-lg relative overflow-x-auto [&::-webkit-scrollbar]:h-2 [&::-webkit-scrollbar-thumb]:rounded-none [&::-webkit-scrollbar-track]:bg-scrollbar-track [&::-webkit-scrollbar-thumb]:bg-scrollbar-thumb";
const KOPFZELLE = "px-6 py-3 text-start text-xs font-medium text-muted-foreground-1 uppercase";
const ZELLE = "px-6 py-3 text-sm text-foreground";
const NAMENSZELLE = "px-6 py-3 text-sm text-foreground text-start font-normal";
const AKTIONSZELLE = "relative z-10 px-6 py-3 text-end";
const HAUPT_LINK =
  "block font-medium text-foreground hover:text-primary focus:outline-hidden focus-visible:underline after:absolute after:inset-0";
/** Feste, vollständige Klassenketten je Einrückungsstufe (nie zusammengesetzt). */
const EINRUECKUNG = [
  "flex items-center gap-x-2",
  "ms-3 ps-3 border-s border-line-2 flex items-center gap-x-2",
  "ms-6 ps-3 border-s border-line-2 flex items-center gap-x-2",
  "ms-9 ps-3 border-s border-line-2 flex items-center gap-x-2",
] as const;
const EINRUECKUNG_MOBIL = [
  "flex items-start gap-x-2",
  "ms-4 ps-3 border-s border-line-2 flex items-start gap-x-2",
  "ms-8 ps-3 border-s border-line-2 flex items-start gap-x-2",
  "ms-12 ps-3 border-s border-line-2 flex items-start gap-x-2",
] as const;
const STATUS_EINRUECKUNG = ["", "ms-3 ps-3", "ms-6 ps-3", "ms-9 ps-3"] as const;

/** Ab dieser Ebene bleibt die Einrückung stehen und die Zweitzeile trägt den Pfad (M-003 T5). */
const FLACH_AB_EBENE = 5;

function stufeVon(ebene: number): 0 | 1 | 2 | 3 {
  return (Math.min(ebene, 4) - 1) as 0 | 1 | 2 | 3;
}

function Chevron({ offen }: { offen: boolean }) {
  return (
    <svg
      className={offen ? "shrink-0 size-4 rotate-90" : "shrink-0 size-4"}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="m9 18 6-6-6-6" />
    </svg>
  );
}

/** Griff zum Ziehen (nur Maus; die Tastatur ordnet über Alt + Pfeile, jede Person mit Recht auch über das Zeilenmenü). Lucide `GripVertical`. */
function Griff() {
  return (
    <svg className="shrink-0 size-4" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="9" cy="12" r="1" />
      <circle cx="9" cy="5" r="1" />
      <circle cx="9" cy="19" r="1" />
      <circle cx="15" cy="12" r="1" />
      <circle cx="15" cy="5" r="1" />
      <circle cx="15" cy="19" r="1" />
    </svg>
  );
}

/** Feste, vollständige Klassenketten je Ablage-Zone (Markierung der Zielzeile) und für die gezogene Zeile. */
const ZONE_KLASSE: Record<DropZone, string> = {
  davor: "relative border-t-2 border-t-primary",
  danach: "relative border-b-2 border-b-primary",
  kind: "relative bg-muted",
};
const GRIFF_KLASSE =
  "shrink-0 size-7.5 inline-flex justify-center items-center rounded-lg text-muted-foreground-1 cursor-grab hover:bg-muted-hover active:cursor-grabbing";
const GRIFFZELLE = "ps-4 pe-0 py-3 w-10";
const FEHLSCHLAG_STANDARD = "Das hat nicht geklappt. Bitte erneut versuchen.";

function Spinner() {
  return (
    <span className="animate-spin inline-block size-3.5 border-3 border-current border-t-transparent text-muted-foreground-1 rounded-full" aria-hidden="true" />
  );
}

export function BaumTabelle({
  beschriftung,
  namensSpalte,
  spalten,
  wurzeln,
  kinderEinheit = { eins: "Kind", viele: "Kinder" },
  leerText = "Keine Einträge.",
  onNachladen,
  anfangsOffen,
  ordnen,
}: {
  /** Name der Tabelle (für `aria-label` und `<caption class="sr-only">`). */
  beschriftung: string;
  namensSpalte: string;
  /** Köpfe der weiteren Spalten, in der Reihenfolge von `BaumKnoten.zellen`. */
  spalten: string[];
  wurzeln: BaumKnoten[];
  kinderEinheit?: { eins: string; viele: string };
  leerText?: string;
  /**
   * Lädt die Kinder eines Knotens mit `ungeladen > 0` beim ersten Aufklappen. Fehlschlag = Promise lehnt ab
   * (Satz „… konnten nicht geladen werden. Bitte erneut versuchen.“ ohne Fehlercode, Knopf „Erneut laden“).
   * Der Aufrufer prüft dabei dieselbe Leseberechtigung wie die Action (r109, r110: Unlesbares fehlt still).
   */
  onNachladen?: (id: string) => Promise<BaumKnoten[]>;
  /**
   * Beim ersten Anzeigen aufgeklappte Knoten (M-004 TP1/TP2: Kontext-Eltern eines Filters, Zweig aus der Kinder-Pille).
   * Ihre Kinder müssen dafür schon in `kinder` stehen; der Baustein lädt für sie nichts nach.
   */
  anfangsOffen?: readonly string[];
  /**
   * Ordnen per Ziehen (Griff, nur Maus) und per Tastatur (Alt + Pfeile auf der fokussierten Zeile); additiv (P-W2 Teil 2,
   * Abweichung AW-046). Der Baustein übersetzt die Gesten in `BaumVerschiebung`en und zeigt Ansage und Satz; die Arithmetik und
   * der Schreibweg gehören dem Aufrufer, der nur Personen mit Recht `ordnen` übergibt (Anbieten = Erlauben). Ohne `ordnen`
   * ist die Tabelle unverändert.
   */
  ordnen?: BaumOrdnen;
}) {
  const [offen, setOffen] = useState<ReadonlySet<string>>(() => new Set(anfangsOffen ?? []));
  const [zieh, setZieh] = useState<{ id: string; ueberId: string | null; zone: DropZone | null } | null>(null);
  const [beschaeftigt, setBeschaeftigt] = useState(false);
  const [ordnenMeldung, setOrdnenMeldung] = useState<string | null>(null);
  const [ansage, setAnsage] = useState("");
  const fokusNach = useRef<string | null>(null);
  const [fokusId, setFokusId] = useState<string | null>(null);
  const [nachgeladen, setNachgeladen] = useState<ReadonlyMap<string, BaumKnoten[]>>(() => new Map());
  const [ladend, setLadend] = useState<ReadonlySet<string>>(() => new Set());
  const [fehlgeschlagen, setFehlgeschlagen] = useState<ReadonlySet<string>>(() => new Set());
  const zeilenRefs = useRef(new Map<string, HTMLTableRowElement>());
  const zeilen = sichtbareZeilen(wurzeln, offen, nachgeladen);
  const aktuellerFokus = fokusId && zeilen.some((z) => z.knoten.id === fokusId) ? fokusId : (zeilen[0]?.knoten.id ?? null);
  const mitAktionen = zeilen.some((z) => z.knoten.aktionen);
  const nichtZiehbar: ReadonlySet<string> = new Set(ordnen?.nichtZiehbar ?? []);

  const lade = async (id: string) => {
    if (!onNachladen) return;
    setLadend((alt) => new Set(alt).add(id));
    setFehlgeschlagen((alt) => {
      const neu = new Set(alt);
      neu.delete(id);
      return neu;
    });
    try {
      const kinder = await onNachladen(id);
      setNachgeladen((alt) => new Map(alt).set(id, kinder));
    } catch {
      setFehlgeschlagen((alt) => new Set(alt).add(id));
    } finally {
      setLadend((alt) => {
        const neu = new Set(alt);
        neu.delete(id);
        return neu;
      });
    }
  };

  const oeffne = (id: string) => {
    setOffen((alt) => new Set(alt).add(id));
    const z = zeilen.find((r) => r.knoten.id === id);
    if (z && !nachgeladen.has(id) && z.knoten.kinder.length === 0 && (z.knoten.ungeladen ?? 0) > 0 && !ladend.has(id)) {
      void lade(id);
    }
  };

  const schliesse = (id: string) =>
    setOffen((alt) => {
      const neu = new Set(alt);
      neu.delete(id);
      return neu;
    });

  const umschalten = (id: string) => (offen.has(id) ? schliesse(id) : oeffne(id));

  const fokussiere = (id: string) => {
    setFokusId(id);
    zeilenRefs.current.get(id)?.focus();
  };

  // Nach einer Verschiebung wandert die Zeile im Baum (neue Daten): der Fokus folgt ihr, sonst ginge er verloren.
  useEffect(() => {
    const id = fokusNach.current;
    if (!id) return;
    fokusNach.current = null;
    zeilenRefs.current.get(id)?.focus();
  }, [wurzeln]);

  const verschiebe = async (v: BaumVerschiebung) => {
    if (!ordnen || beschaeftigt) return;
    // Anbieten = Erlauben (r110): was der Aufrufer nicht anbietet, führt auch die Tastatur nicht aus.
    if (nichtZiehbar.has(v.id) || (ordnen.erlaubt && !ordnen.erlaubt(v))) return;
    setBeschaeftigt(true);
    setOrdnenMeldung(null);
    try {
      const r = await ordnen.beiVerschieben(v);
      if (r.ok) {
        setAnsage(r.ansage ?? "");
        fokusNach.current = v.id;
        zeilenRefs.current.get(v.id)?.focus();
        // Die Zeile landet unter einer anderen: deren Zweig öffnen, damit sie sichtbar bleibt (nie schließt der Baustein etwas).
        if (v.art === "kind") oeffne(v.zielId);
        if (v.art === "tiefer") {
          const z = zeilen.find((s) => s.knoten.id === v.id);
          const vorgaenger = z && zeilen.find((s) => s.elternId === z.elternId && s.position === z.position - 1);
          if (vorgaenger) oeffne(vorgaenger.knoten.id);
        }
      } else {
        setOrdnenMeldung(r.meldung);
      }
    } catch (fehler) {
      console.error("Verschieben fehlgeschlagen", fehler);
      setOrdnenMeldung(FEHLSCHLAG_STANDARD);
    } finally {
      setBeschaeftigt(false);
      setZieh(null);
    }
  };

  if (wurzeln.length === 0) {
    return (
      <p className="p-5 text-sm text-muted-foreground-1" role="status">
        {leerText}
      </p>
    );
  }

  const zaehlText = (z: SichtbareZeile) => `${z.kinderAnzahl} ${z.kinderAnzahl === 1 ? kinderEinheit.eins : kinderEinheit.viele}`;
  const ladeSatz = `${kinderEinheit.viele} werden geladen …`;
  const fehlerSatz = `Die ${kinderEinheit.viele} konnten nicht geladen werden. Bitte erneut versuchen.`;

  const aufklappKnopf = (z: SichtbareZeile) => {
    const k = z.knoten;
    const istOffen = offen.has(k.id);
    return (
      <Knopf
        variante="ghost"
        nurSymbol
        groesse="xs"
        ueberZeilenlink
        starr
        ariaExpanded={istOffen}
        label={`${istOffen ? "Zuklappen" : "Aufklappen"}: ${k.name}`}
        symbol={<Chevron offen={istOffen} />}
        onClick={() => umschalten(k.id)}
      />
    );
  };

  const namenBlock = (z: SichtbareZeile) => {
    const k = z.knoten;
    const zweit = z.ebene >= FLACH_AB_EBENE ? `Ebene ${z.ebene} · ${[...z.pfad, k.name].join(" › ")}` : k.zweitzeile;
    return (
      <span className="min-w-0">
        <span className="block">{k.href ? <Link href={k.href} className={HAUPT_LINK}>{k.name}</Link> : <span className="font-medium text-foreground">{k.name}</span>}</span>
        {zweit && <span className="block text-xs text-muted-foreground-1">{zweit}</span>}
      </span>
    );
  };

  const zeilenStatus = (z: SichtbareZeile) => {
    const id = z.knoten.id;
    if (!offen.has(id)) return null;
    const stufe = Math.min(z.ebene + 1, 4) - 1;
    if (ladend.has(id)) {
      return (
        <tr key={`${id}-status`}>
          <td colSpan={spalten.length + 1 + (mitAktionen ? 1 : 0) + (ordnen ? 1 : 0)} className={ZELLE}>
            <div role="status" className={cx(STATUS_EINRUECKUNG[stufe] ?? "", "flex items-center gap-x-2 text-muted-foreground-1")}>
              <Spinner />
              <span>{ladeSatz}</span>
            </div>
          </td>
        </tr>
      );
    }
    if (fehlgeschlagen.has(id)) {
      return (
        <tr key={`${id}-status`}>
          <td colSpan={spalten.length + 1 + (mitAktionen ? 1 : 0) + (ordnen ? 1 : 0)} className={ZELLE}>
            <div role="alert" className={cx(STATUS_EINRUECKUNG[stufe] ?? "", "flex flex-wrap items-center gap-x-2 gap-y-1")}>
              <Zustandspille ton="gefahr" mitPunkt>
                Nicht geladen
              </Zustandspille>
              <span>{fehlerSatz}</span>
              <Knopf groesse="xs" onClick={() => void lade(id)}>
                Erneut laden
              </Knopf>
            </div>
          </td>
        </tr>
      );
    }
    return null;
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Knopf groesse="sm" onClick={() => setOffen(alleAufklappen(wurzeln, nachgeladen))}>
          Alle aufklappen
        </Knopf>
        <Knopf groesse="sm" onClick={() => setOffen(new Set())}>
          Alle zuklappen
        </Knopf>
        {/* Nur mit Tabelle (ab md): die Karten darunter haben keinen Griff, der Satz „Griff ziehen“ wäre dort falsch (Befund B11); Ordnen läuft in den Karten über das Zeilenmenü. */}
        {ordnen?.hinweis && <p className="hidden md:block text-xs text-muted-foreground-1">{ordnen.hinweis}</p>}
      </div>
      {ordnen && (
        <>
          {/* Ansage für Hilfstechnik nach einer Verschiebung; der Satz bei Ablehnung/Fehlschlag steht an der Fläche des Vorgangs (r105) */}
          <p className="sr-only" aria-live="polite">
            {ansage}
          </p>
          {ordnenMeldung && <Hinweissatz>{ordnenMeldung}</Hinweissatz>}
        </>
      )}
      {/* Preline: component/tables/tables-rounded-tables (Baum: component/tree-view/tree-view-customize) */}
      <div className="hidden md:block min-w-full">
        <div className={RAHMEN}>
          <table className="min-w-full" role="treegrid" aria-label={beschriftung}>
            <caption className="sr-only">{beschriftung}</caption>
            <thead>
              <tr>
                {ordnen && (
                  <th scope="col" className={KOPFZELLE}>
                    <span className="sr-only">Verschieben</span>
                  </th>
                )}
                <th scope="col" className={KOPFZELLE}>
                  {namensSpalte}
                </th>
                {spalten.map((s) => (
                  <th key={s} scope="col" className={KOPFZELLE}>
                    {s}
                  </th>
                ))}
                {mitAktionen && (
                  <th scope="col" className={KOPFZELLE}>
                    <span className="sr-only">Aktionen</span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-table-line">
              {zeilen.flatMap((z) => {
                const k = z.knoten;
                const hatKinder = z.kinderAnzahl > 0;
                const zugeklappteKinder = hatKinder && !offen.has(k.id);
                const ueberDiese = zieh !== null && zieh.ueberId === k.id ? zieh.zone : null;
                const wirdGezogen = zieh !== null && zieh.id === k.id;
                const reihe = (
                  <tr
                    key={k.id}
                    ref={(el) => {
                      if (el) zeilenRefs.current.set(k.id, el);
                      else zeilenRefs.current.delete(k.id);
                    }}
                    aria-level={z.ebene}
                    aria-posinset={z.position}
                    aria-setsize={z.geschwister}
                    tabIndex={k.id === aktuellerFokus ? 0 : -1}
                    className={
                      ueberDiese
                        ? cx(ZONE_KLASSE[ueberDiese], "focus:outline-hidden")
                        : wirdGezogen
                          ? "relative opacity-50 focus:outline-hidden focus:bg-muted"
                          : "relative focus:outline-hidden focus:bg-muted"
                    }
                    onFocus={(e) => {
                      if (e.target === e.currentTarget) setFokusId(k.id);
                    }}
                    onDragOver={
                      ordnen
                        ? (e) => {
                            // Nur ablegen, was kein Zyklus wäre (nicht auf sich selbst oder einen Nachfahren).
                            if (!zieh || zieh.id === k.id || istNachfahre(wurzeln, zieh.id, k.id)) return;
                            const zone = dropZone(e.currentTarget.getBoundingClientRect(), e.clientY, zugeklappteKinder);
                            // Nur anbieten, was der Schreibweg annähme (r110): sonst keine Ablage und keine Markierung.
                            if (ordnen.erlaubt && !ordnen.erlaubt({ art: zone, id: zieh.id, zielId: k.id })) {
                              if (zieh.ueberId === k.id) setZieh({ ...zieh, ueberId: null, zone: null });
                              return;
                            }
                            e.preventDefault();
                            if (zieh.ueberId !== k.id || zieh.zone !== zone) setZieh({ ...zieh, ueberId: k.id, zone });
                          }
                        : undefined
                    }
                    onDragLeave={
                      ordnen
                        ? () => {
                            if (zieh && zieh.ueberId === k.id) setZieh({ ...zieh, ueberId: null, zone: null });
                          }
                        : undefined
                    }
                    onDrop={
                      ordnen
                        ? (e) => {
                            e.preventDefault();
                            if (!zieh || !zieh.zone || zieh.ueberId !== k.id) return;
                            const { id, zone } = zieh;
                            void verschiebe({ art: zone, id, zielId: k.id });
                          }
                        : undefined
                    }
                    onKeyDown={(e) => {
                      // Alt + Pfeil ordnet (auch aus Link und Menü der Zeile heraus); einfache Pfeile bleiben die Baum-Navigation.
                      if (ordnen && e.altKey) {
                        const art = tasteZuVerschiebung(e.key, true);
                        if (art) {
                          e.preventDefault();
                          void verschiebe({ art, id: k.id });
                          return;
                        }
                      }
                      if (e.target !== e.currentTarget) return;
                      const a = tasteZuAktion(e.key, k.id, zeilen, offen);
                      if (a.art === "nichts") return;
                      e.preventDefault();
                      if (a.art === "fokus") fokussiere(a.id);
                      else if (a.art === "oeffnen") oeffne(a.id);
                      else schliesse(a.id);
                    }}
                  >
                    {ordnen && (
                      <td className={GRIFFZELLE}>
                        {/* Gesperrte Zeile (nichtZiehbar): Griff-Zelle bleibt leer, die Zeile lässt sich nicht greifen (r110). */}
                        {!nichtZiehbar.has(k.id) && (
                          <span
                            draggable={!beschaeftigt}
                            aria-hidden="true"
                            className={GRIFF_KLASSE}
                            data-baum-griff={k.id}
                            onDragStart={(e) => {
                              // Die ganze Zeile als Zieh-Bild (nicht nur der Griff); dataTransfer fehlt in manchen Umgebungen.
                              const reihe = zeilenRefs.current.get(k.id);
                              if (e.dataTransfer) {
                                e.dataTransfer.effectAllowed = "move";
                                e.dataTransfer.setData("text/plain", k.id);
                                if (reihe) e.dataTransfer.setDragImage(reihe, 0, 0);
                              }
                              setZieh({ id: k.id, ueberId: null, zone: null });
                            }}
                            onDragEnd={() => setZieh(null)}
                          >
                            <Griff />
                          </span>
                        )}
                      </td>
                    )}
                    <th scope="row" className={NAMENSZELLE}>
                      <div className={EINRUECKUNG[stufeVon(z.ebene)]}>
                        {hatKinder ? aufklappKnopf(z) : <span className="shrink-0 size-7.5" aria-hidden="true" />}
                        {namenBlock(z)}
                        {hatKinder && <Badge ton="gedaempft">{zaehlText(z)}</Badge>}
                      </div>
                    </th>
                    {k.zellen.map((zelle, i) => (
                      <td key={i} className={ZELLE}>
                        {zelle}
                      </td>
                    ))}
                    {mitAktionen && <td className={AKTIONSZELLE}>{k.aktionen}</td>}
                  </tr>
                );
                const status = zeilenStatus(z);
                return status ? [reihe, status] : [reihe];
              })}
            </tbody>
          </table>
        </div>
      </div>
      {/* Mobil (T12): Karten aus denselben sichtbaren Zeilen */}
      <ul className="md:hidden border border-table-line rounded-lg divide-y divide-table-line">
        {zeilen.flatMap((z) => {
          const k = z.knoten;
          const hatKinder = z.kinderAnzahl > 0;
          const id = k.id;
          const karte = (
            <li key={`m-${id}`} className="relative px-4 py-3">
              <div className={EINRUECKUNG_MOBIL[stufeVon(z.ebene)]}>
                {hatKinder ? aufklappKnopf(z) : <span className="shrink-0 size-7.5" aria-hidden="true" />}
                <div className="min-w-0 grow">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    {namenBlock(z)}
                    {hatKinder && <Badge ton="gedaempft">{zaehlText(z)}</Badge>}
                  </div>
                  {spalten.length > 0 && (
                    <dl className="mt-2 space-y-1">
                      {spalten.map((s, i) => (
                        <div key={s} className="flex items-center justify-between gap-x-3 text-sm">
                          <dt className="text-muted-foreground-1">{s}</dt>
                          <dd className="min-w-0 text-foreground text-end">{k.zellen[i]}</dd>
                        </div>
                      ))}
                    </dl>
                  )}
                </div>
                {k.aktionen && <div className="relative z-10 shrink-0">{k.aktionen}</div>}
              </div>
            </li>
          );
          const status =
            offen.has(id) && ladend.has(id) ? (
              <li key={`m-${id}-status`} className="px-4 py-3 text-sm text-muted-foreground-1" aria-live="polite">
                <span className="flex items-center gap-x-2">
                  <Spinner />
                  <span>{ladeSatz}</span>
                </span>
              </li>
            ) : offen.has(id) && fehlgeschlagen.has(id) ? (
              <li key={`m-${id}-status`} className="px-4 py-3 text-sm flex flex-wrap items-center gap-x-2 gap-y-1" aria-live="assertive">
                <Zustandspille ton="gefahr" mitPunkt>
                  Nicht geladen
                </Zustandspille>
                <span>{fehlerSatz}</span>
                <Knopf groesse="xs" onClick={() => void lade(id)}>
                  Erneut laden
                </Knopf>
              </li>
            ) : null;
          return status ? [karte, status] : [karte];
        })}
      </ul>
    </div>
  );
}
